import React from "react";

/**
 * LOM TimeTrack brand mark.
 * A rounded-square badge with a two-hand clock in Jamaican green (#00A651)
 * and gold (#FFCC00), matching the LOM Solutions brand family.
 *
 * Usage:
 *   <TimeTrackLogo size={40} />                       // icon only
 *   <TimeTrackLogo size={40} variant="lockup" />      // icon + wordmark
 *   <TimeTrackLogo size={40} variant="lockup" invert />  // for dark backgrounds
 */
export default function TimeTrackLogo({
  size = 40,
  variant = "icon", // 'icon' | 'lockup'
  invert = false,   // white text for dark backgrounds
  showParent = true,
  className = "",
}) {
  const iconBg = "#09090b";       // deep black, matches app
  const green = "#00A651";        // Jamaican green
  const gold = "#FFCC00";         // Jamaican gold
  const face = "#ffffff";
  const wordColor = invert ? "#ffffff" : "#09090b";
  const parentColor = invert ? "rgba(255,255,255,0.55)" : "#71717a";

  const icon = (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="LOM TimeTrack"
      className="flex-shrink-0"
    >
      {/* Rounded-square badge */}
      <rect x="0" y="0" width="64" height="64" rx="12" fill={iconBg} />
      {/* Gold arc — subtle brand accent */}
      <path
        d="M 32 12 A 20 20 0 0 1 52 32"
        stroke={gold}
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
        opacity="0.9"
      />
      {/* Clock face ring */}
      <circle cx="32" cy="32" r="18" stroke={face} strokeWidth="1.5" fill="none" opacity="0.28" />
      {/* Hour hand — gold, pointing to 2 */}
      <line
        x1="32" y1="32"
        x2="42.4" y2="26"
        stroke={gold}
        strokeWidth="3.5"
        strokeLinecap="round"
      />
      {/* Minute hand — green, pointing to 12 */}
      <line
        x1="32" y1="32"
        x2="32" y2="16"
        stroke={green}
        strokeWidth="3.5"
        strokeLinecap="round"
      />
      {/* Pivot dot */}
      <circle cx="32" cy="32" r="2.6" fill={green} />
      {/* Small 12 o'clock tick */}
      <circle cx="32" cy="12" r="1.6" fill={gold} />
    </svg>
  );

  if (variant === "icon") return <div className={className}>{icon}</div>;

  const wordSize = Math.round(size * 0.55);
  const parentSize = Math.round(size * 0.22);
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {icon}
      <div className="leading-none">
        <div
          className="font-heading font-black tracking-tight"
          style={{ color: wordColor, fontSize: `${wordSize}px`, lineHeight: 1 }}
        >
          LOM<span style={{ color: green }}>Time</span>Track
        </div>
        {showParent && (
          <div
            className="font-heading font-medium uppercase mt-1.5"
            style={{
              color: parentColor,
              fontSize: `${parentSize}px`,
              letterSpacing: "0.25em",
            }}
          >
            A LOM Solutions product
          </div>
        )}
      </div>
    </div>
  );
}
